import 'overlayscrollbars/overlayscrollbars.css';
import {
  css, type Interpolation,
} from '@emotion/react';
import type { EventListeners } from 'overlayscrollbars';
import {
  OverlayScrollbarsComponent, type OverlayScrollbarsComponentRef,
} from 'overlayscrollbars-react';
import React, {
  type ComponentProps,
  type CSSProperties, forwardRef, type MouseEvent, useMemo,
} from 'react';
import type { Theme } from '~/_shared/themes/theme.model';
import { ScrollbarType } from './scrollbar.component';

const SCROLLBAR_WIDTH = 7;
const SCROLLBAR_ACTIVE_WIDTH = 11;

type OverlayScrollbarProps = Readonly<{
  className?: string;
  isScrollbarHidden?: boolean;
  maxHeight?: number | string;
  type: ScrollbarType | ScrollbarType[];
  scrollbarStyle?: Interpolation;
  onClick?: (e: MouseEvent) => void;
  onUpdate?: EventListeners['scroll'];
  children?: React.ReactNode;
  style?: CSSProperties;
}>;

const baseTransition = 'visibility 0s 1s, opacity 1s linear, height .15s, width .15s';// fade out
const noVisibilityNorOpacityTransition = 'visibility 0s, opacity 0s, height .15s, width .15s'; // no fade in

type ScrollbarStyleProps = {
  maxHeight?: number | string;
  isScrollbarHidden?: boolean;
};

const scrollbarStyle = ({ isScrollbarHidden }: ScrollbarStyleProps) => (theme: Theme) => css([
  {
    minWidth: '100%',
  },
  {
    '& .os-scrollbar': {
      '--os-handle-max-size': '150px',
      '--os-padding-axis': '3px',
      visibility: isScrollbarHidden ? 'hidden' : 'visible',
      zIndex: 999,
      transition: baseTransition, // fade out
      '& .os-scrollbar-handle': {
        transition: baseTransition, // fade out
      },
    },
    '& .os-scrollbar-vertical': {
      width: SCROLLBAR_WIDTH,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'end',
      '&:hover, &:active': {
        width: SCROLLBAR_ACTIVE_WIDTH,
      },
      '& .os-scrollbar-handle': {
        width: SCROLLBAR_WIDTH,
        '&:hover, &:active': {
          width: SCROLLBAR_ACTIVE_WIDTH,
        },
      },
    },
    '& .os-scrollbar-horizontal': {
      height: SCROLLBAR_WIDTH,
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'end',
      '&:hover, &:active': {
        height: SCROLLBAR_ACTIVE_WIDTH,
      },
      '& .os-scrollbar-handle': {
        height: SCROLLBAR_WIDTH,
        '&:hover, &:active': {
          height: SCROLLBAR_ACTIVE_WIDTH,
        },
      },
    },
    '&:hover .os-scrollbar, .os-scrollbar:hover, .os-scrollbar:active': {
      transition: noVisibilityNorOpacityTransition, // fade in immediately
      opacity: 1,
    },
    '.os-scrollbar .os-scrollbar-handle': {
      opacity: 0.5,
      transition: noVisibilityNorOpacityTransition, // fade in immediately
      backgroundColor: theme.scrollbarColors.thumbColor,
      cursor: 'default',
      borderRadius: 5000, // Makes ends rounds.
    },
    '& .os-scrollbar:hover .os-scrollbar-handle': {
      opacity: 1,
      transition: noVisibilityNorOpacityTransition, // fade in immediately
    },
    '&:hover .os-scrollbar .os-scrollbar-handle.dragging, .os-scrollbar:hover .os-scrollbar-handle.dragging': {
      opacity: 0.35,
    },
    '[data-overlayscrollbars-viewport*="overflowYHidden"] ~ .os-scrollbar-vertical': {
      display: 'none',
    },
    '[data-overlayscrollbars-viewport*="overflowXHidden"] ~ .os-scrollbar-horizontal': {
      display: 'none',
    },
  }]);

export const OverlayScrollbarComponent = forwardRef<OverlayScrollbarsComponentRef, OverlayScrollbarProps>(({
  className,
  isScrollbarHidden,
  maxHeight,
  type,
  scrollbarStyle: customScrollbarStyle,
  onClick,
  onUpdate,
  children,
  style,
}, scrollbarRef) => {

  const options: ComponentProps<typeof OverlayScrollbarsComponent>['options'] = useMemo(() => {
    const scrollbarTypes: ScrollbarType[] = Array.isArray(type) ? type : [type];
    return {
      scrollbars: {
        theme: 'os-theme-custom',
        autoHide: 'leave',
        autoHideDelay: 0,
        clickScroll: true,
        visibility: isScrollbarHidden ? 'hidden' : 'auto',
      },
      overflow: {
        x: scrollbarTypes.includes(ScrollbarType.Horizontal) ? 'scroll' : 'hidden',
        y: scrollbarTypes.includes(ScrollbarType.Vertical) ? 'scroll' : 'hidden',
      },
      update: {
        elementEvents: [['*', 'animationend'], ['*', 'transitionend']],
      },
    };
  }, [isScrollbarHidden, type]);

  const events = useMemo(() => {
    const scroll: EventListeners['scroll'] = (instance, event) => {
      if (Array.isArray(onUpdate)) {
        onUpdate.forEach(fn => fn(instance, event));
      }
      else {
        onUpdate?.(instance, event);
      }
    };
    return {
      scroll,
    };
  }, [onUpdate]);

  return (
    <OverlayScrollbarsComponent
      ref={scrollbarRef}
      options={options}
      css={[customScrollbarStyle, scrollbarStyle({ isScrollbarHidden })]}
      style={{
        ...style,
        maxHeight,
      }}
      className={className}
      events={events}
      onClick={onClick}
    >
      {children}
    </OverlayScrollbarsComponent>
  );
});

OverlayScrollbarComponent.displayName = 'OverlayScrollbarComponent';
